import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Chip,
  Grid,
  TableSortLabel,
  Tooltip,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { breedingAPI, horsesAPI, mareOwnersAPI, referenceAPI } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { calculateDaysInFoal, formatMonthsAndDays } from '../utils/breedingDates';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import MareGestationHistoryTooltip from '../components/MareGestationHistoryTooltip';
import useRefreshOnFocus from '../utils/useRefreshOnFocus';
import { actionsKabobCellSx } from '../theme/tableStyles';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import AddHorseModal from '../components/AddHorseModal';
import AddMareOwnerModal from '../components/AddMareOwnerModal';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const STICKY_BG = 'background.paper';
// Only Actions and Mare are pinned. Everything else scrolls, so the frozen block
// stays inside a portrait phone viewport (~390px) and the remaining columns have
// somewhere to appear.
const ACTIONS_COLUMN_WIDTH = 56;
// Mare barn names are short; cap the column rather than letting a long one widen
// the pinned block, since the `left` offset below is a fixed number.
const MARE_COLUMN_WIDTH = { xs: 120, sm: 160 };
const stickyActionsSx = {
  ...actionsKabobCellSx,
  position: 'sticky', left: 0, zIndex: 4, backgroundColor: STICKY_BG, minWidth: ACTIONS_COLUMN_WIDTH,
};
const stickyActionsHeaderSx = { ...stickyActionsSx, zIndex: 8 };
const stickyMareSx = {
  position: 'sticky', left: ACTIONS_COLUMN_WIDTH, zIndex: 4, backgroundColor: STICKY_BG, whiteSpace: 'nowrap',
  minWidth: MARE_COLUMN_WIDTH, maxWidth: MARE_COLUMN_WIDTH, overflow: 'hidden', textOverflow: 'ellipsis',
};
const stickyMareHeaderSx = { ...stickyMareSx, zIndex: 8 };

const OWNER_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All Owner Types' },
  { value: 'owned', label: 'Owned' },
  { value: 'leased', label: 'Leased' },
  { value: 'client', label: 'Client' },
];

// Sort by last name (last word) then first name (first word)
const sortMareOwnersByLastName = (a, b) => {
  const last = (n) => (n || '').trim().split(/\s+/).pop() || '';
  const first = (n) => (n || '').trim().split(/\s+/).shift() || '';
  const cmp = last(a.name).localeCompare(last(b.name));
  return cmp !== 0 ? cmp : first(a.name).localeCompare(first(b.name));
};

const BreedingPage = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const [breedingRecords, setBreedingRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  // Per-mare gestation history for the due-date hover popup, keyed by mare_id.
  // Fetched lazily on first hover; cleared whenever the record list reloads.
  const [mareHistory, setMareHistory] = useState({});
  // Mares already requested, so hovering several rows of the same mare fetches once.
  const requestedMareHistory = useRef(new Set());

  // Filter states
  const [filterSeason, setFilterSeason] = useState(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOwnerType, setFilterOwnerType] = useState('owned');
  
  // Sort states
  const [sortBy, setSortBy] = useState('breeding_date');
  const [sortOrder, setSortOrder] = useState('asc');

  // Reference data
  const [mares, setMares] = useState([]);
  const [stallions, setStallions] = useState([]);
  const [mareOwners, setMareOwners] = useState([]);
  const [breedingMethods, setBreedingMethods] = useState([]);
  const [pregnancyStatuses, setPregnancyStatuses] = useState([]);

  // Modal states
  const [openMareModal, setOpenMareModal] = useState(false);
  const [openStallionModal, setOpenStallionModal] = useState(false);
  const [openMareOwnerModal, setOpenMareOwnerModal] = useState(false);

  const [formData, setFormData] = useState({
    mare_id: '',
    stallion_id: '',
    mare_owner_id: '',
    breeding_dates: [],
    breeding_start_date: '',
    breeding_end_date: '',
    breeding_method: '',
    breeding_season: new Date().getFullYear(),
    confirmed_in_foal_date: '',
    actual_foaling_date: '',
    pregnancy_status: 'bred',
    heart_beat_check: '',
    forty_five_day_check: '',
    sixty_day_check: '',
    aqha_sbr: '',
    apha_sbr: '',
    stud_fee: '',
    stud_fee_paid_date: '',
    booking_fee: '',
    booking_fee_paid_date: '',
    mare_care_cost: '',
    transport_cost: '',
    notes: '',
  });

  const confirmedInFoalHasDate = Boolean(
    formData.confirmed_in_foal_date && String(formData.confirmed_in_foal_date).trim()
  );
  const hasBreedingDates = (formData.breeding_dates || []).filter(
    (d) => d && String(d).trim()
  ).length > 0;
  const confirmedInFoalMissingRequiredDates = confirmedInFoalHasDate && !hasBreedingDates;

  const confirmedInFoalHelperText = confirmedInFoalMissingRequiredDates
      ? 'Confirmed in foal requires at least one breeding date'
      : '';

  const confirmedInFoalValid = confirmedInFoalHasDate && !confirmedInFoalMissingRequiredDates;

  useEffect(() => {
    // Keep dropdown aligned with backend rules while still allowing users to see/choose invalid states.
    if (confirmedInFoalValid) {
      if (editingRecord) {
        // On update: only auto-set to confirmed when current status is not final.
        const current = (formData.pregnancy_status || '').toString().toLowerCase();
        if ((current === 'bred' || current === 'open') && formData.pregnancy_status !== 'confirmed') {
          setFormData((prev) => ({ ...prev, pregnancy_status: 'confirmed' }));
        }
      } else {
        // On create: confirmed-in-foal implies confirmed when dates are valid.
        if (formData.pregnancy_status !== 'confirmed') {
          setFormData((prev) => ({ ...prev, pregnancy_status: 'confirmed' }));
        }
      }
      return;
    }

    // When confirmed date is empty, only on create we force status to bred if breeding dates exist.
    if (!confirmedInFoalHasDate && !editingRecord && hasBreedingDates) {
      if (formData.pregnancy_status !== 'bred') {
        setFormData((prev) => ({ ...prev, pregnancy_status: 'bred' }));
      }
    }
  }, [
    confirmedInFoalValid,
    confirmedInFoalHasDate,
    hasBreedingDates,
    editingRecord,
    formData.pregnancy_status,
  ]);

  // Load reference data
  useEffect(() => {
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    const [maresRes, stallionsRes, ownersRes, methodsRes, statusesRes] = await Promise.allSettled([
      horsesAPI.getAll({ type: 'mare' }),
      horsesAPI.getAll({ type: 'stallion' }),
      mareOwnersAPI.getAll({ active: true }),
      referenceAPI.getBreedingMethods(),
      referenceAPI.getPregnancyStatuses(),
    ]);
    if (maresRes.status === 'fulfilled') {
      setMares(toArray(maresRes.value?.data, 'mares').filter(mare => mare.owner_type !== 'reference'));
    } else {
      console.error('Error loading mares:', maresRes.reason);
    }
    if (stallionsRes.status === 'fulfilled') {
      setStallions(toArray(stallionsRes.value?.data, 'stallions'));
    } else {
      console.error('Error loading stallions:', stallionsRes.reason);
    }
    if (ownersRes.status === 'fulfilled') {
      setMareOwners(toArray(ownersRes.value?.data, 'mare owners'));
    } else {
      console.error('Error loading mare owners:', ownersRes.reason);
    }
    if (methodsRes.status === 'fulfilled') {
      setBreedingMethods(toArray(methodsRes.value?.data, 'breeding methods'));
    } else {
      console.error('Error loading breeding methods:', methodsRes.reason);
    }
    if (statusesRes.status === 'fulfilled') {
      setPregnancyStatuses(toArray(statusesRes.value?.data, 'pregnancy statuses'));
    } else {
      console.error('Error loading pregnancy statuses:', statusesRes.reason);
    }
  };

  // Fetch a mare's gestation history the first time one of her due dates is hovered.
  const handleMareHistoryOpen = useCallback(async (mareId) => {
    if (!mareId || requestedMareHistory.current.has(mareId)) return;
    requestedMareHistory.current.add(mareId);
    setMareHistory((prev) => ({ ...prev, [mareId]: { status: 'loading', rows: [] } }));

    try {
      const response = await breedingAPI.getMareGestationHistory(mareId);
      const rows = Array.isArray(response?.data) ? response.data : [];
      setMareHistory((prev) => ({ ...prev, [mareId]: { status: 'loaded', rows } }));
    } catch (error) {
      console.error('Error loading mare gestation history:', error);
      // Drop the guard so the next hover retries.
      requestedMareHistory.current.delete(mareId);
      setMareHistory((prev) => ({ ...prev, [mareId]: { status: 'error', rows: [] } }));
    }
  }, []);

  const loadBreedingRecords = useCallback(async () => {
    // Records are about to change; cached history could now be stale.
    requestedMareHistory.current.clear();
    setMareHistory({});
    setLoadError('');

    try {
      const params = {};
      if (filterSeason) params.season = filterSeason;
      if (filterStatus) params.status = filterStatus;
      if (filterOwnerType) params.owner_type = filterOwnerType;
      params.sort_by = sortBy;
      params.sort_order = sortOrder;
      
      const response = await breedingAPI.getAll(params);
      const raw = response?.data;
      if (raw != null && !Array.isArray(raw)) {
        console.warn('Breeding API returned non-array payload; using empty list', raw);
      }
      setBreedingRecords(Array.isArray(raw) ? raw : []);
    } catch (error) {
      console.error('Error loading breeding records:', error);
      // The previous list is left in place on purpose: a failed refresh should say so
      // rather than blank the page out into something indistinguishable from "no records".
      setLoadError(error?.response?.data?.error || 'Unable to load breeding records for the selected filters. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filterSeason, filterStatus, filterOwnerType, sortBy, sortOrder]);

  useEffect(() => {
    loadBreedingRecords();
  }, [loadBreedingRecords]);

  useRefreshOnFocus(loadBreedingRecords);

  const handleOpenDialog = (record = null) => {
    if (breedingMethods.length === 0 || mares.length === 0 || stallions.length === 0) {
      loadReferenceData();
    }
    if (record) {
      setEditingRecord(record);
      setFormData({
        mare_id: record.mare_id,
        stallion_id: record.stallion_id,
        mare_owner_id: record.mare_owner_id || '',
        breeding_dates: Array.isArray(record.breeding_dates) && record.breeding_dates.length > 0
          ? [...record.breeding_dates]
          : (record.breeding_date ? [record.breeding_date] : ['']),
        breeding_start_date: record.breeding_start_date || '',
        breeding_end_date: record.breeding_end_date || '',
        breeding_method: record.breeding_method || '',
        breeding_season: record.breeding_season || new Date().getFullYear(),
        confirmed_in_foal_date: record.confirmed_in_foal_date || '',
        actual_foaling_date: record.actual_foaling_date || '',
        pregnancy_status: record.pregnancy_status || 'bred',
        heart_beat_check: record.heart_beat_check || '',
        forty_five_day_check: record.forty_five_day_check || '',
        sixty_day_check: record.sixty_day_check || '',
        aqha_sbr: record.aqha_sbr || '',
        apha_sbr: record.apha_sbr || '',
        stud_fee: record.stud_fee || '',
        stud_fee_paid_date: record.stud_fee_paid_date || '',
        booking_fee: record.booking_fee || '',
        booking_fee_paid_date: record.booking_fee_paid_date || '',
        mare_care_cost: record.mare_care_cost || '',
        transport_cost: record.transport_cost || '',
        notes: record.notes || '',
      });
    } else {
      setEditingRecord(null);
      setFormData({
        mare_id: '',
        stallion_id: '',
        mare_owner_id: '',
        breeding_dates: [''],
        breeding_start_date: '',
        breeding_end_date: '',
        breeding_method: '',
        breeding_season: new Date().getFullYear(),
        confirmed_in_foal_date: '',
        pregnancy_status: 'bred',
        heart_beat_check: '',
        forty_five_day_check: '',
        sixty_day_check: '',
        aqha_sbr: '',
        apha_sbr: '',
        stud_fee: '',
        stud_fee_paid_date: '',
        booking_fee: '',
        booking_fee_paid_date: '',
        mare_care_cost: '',
        transport_cost: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRecord(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'breeding_method') {
        if (!(prev.breeding_dates?.length)) {
          next.breeding_dates = [''];
        }
      }
      return next;
    });
  };

  const handleBreedingDateChange = (index, value) => {
    setFormData((prev) => {
      const arr = [...(prev.breeding_dates || [])];
      arr[index] = value;
      return { ...prev, breeding_dates: arr };
    });
  };

  const handleAddBreedingDate = () => {
    setFormData((prev) => ({
      ...prev,
      breeding_dates: [...(prev.breeding_dates || []), ''],
    }));
  };

  const handleRemoveBreedingDate = (index) => {
    setFormData((prev) => {
      const arr = [...(prev.breeding_dates || [])].filter((_, i) => i !== index);
      return { ...prev, breeding_dates: arr.length ? arr : [''] };
    });
  };

  const handleSubmit = async () => {
    try {
      const dataToSubmit = { ...formData };
      [
        'confirmed_in_foal_date',
        'actual_foaling_date',
        'heart_beat_check',
        'forty_five_day_check',
        'sixty_day_check',
        'aqha_sbr',
        'apha_sbr',
        'stud_fee',
        'stud_fee_paid_date',
        'booking_fee',
        'booking_fee_paid_date',
        'mare_care_cost',
        'transport_cost',
        'mare_owner_id',
      ].forEach((field) => {
        if (dataToSubmit[field] === '') {
          dataToSubmit[field] = null;
        }
      });
      const dates = (formData.breeding_dates || []).filter((d) => d && String(d).trim());
      dataToSubmit.breeding_dates = dates.length ? dates : null;
      dataToSubmit.breeding_start_date = formData.breeding_start_date || null;
      dataToSubmit.breeding_end_date = formData.breeding_end_date || null;

      if (editingRecord) {
        await breedingAPI.update(editingRecord.id, dataToSubmit);
      } else {
        await breedingAPI.create(dataToSubmit);
      }
      handleCloseDialog();
      loadBreedingRecords();
      loadReferenceData();
    } catch (error) {
      console.error('Error saving breeding record:', error);
      const message =
        (error && error.response && error.response.data && error.response.data.error) ||
        (error && error.response && error.response.status ? `Request failed (${error.response.status})` : null) ||
        (error && error.message) ||
        'Error saving breeding record';
      notify.error(message);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (id) => setDeleteTarget(id);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await breedingAPI.delete(deleteTarget);
      setDeleteTarget(null);
      loadBreedingRecords();
    } catch (error) {
      console.error('Error deleting breeding record:', error);
      notify.error(errorMessage(error, 'Error deleting breeding record'));
    }
  };

  // Handle adding new mare
  const handleAddMare = async (newHorse) => {
    await loadReferenceData();
    setFormData((prev) => ({ ...prev, mare_id: newHorse.id }));
    setOpenMareModal(false);
  };

  // Handle adding new stallion
  const handleAddStallion = async (newHorse) => {
    await loadReferenceData();
    setFormData((prev) => ({ ...prev, stallion_id: newHorse.id }));
    setOpenStallionModal(false);
  };

  // Handle adding new mare owner
  const handleAddMareOwner = async (newOwner) => {
    await loadReferenceData();
    setFormData((prev) => ({ ...prev, mare_owner_id: newOwner.id }));
    setOpenMareOwnerModal(false);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'bred':
        return 'default';
      case 'confirmed':
        return 'success';
      case 'open':
        return 'warning';
      case 'foaled':
        return 'info';
      case 'aborted':
        return 'error';
      default:
        return 'default';
    }
  };

  const getHorseDisplayName = (horse) => {
    if (!horse) return 'Unknown';
    const registeredName = horse.registered_name?.trim();
    const barnName = horse.name?.trim();
    
    if (registeredName && barnName) {
      return `${registeredName} (${barnName})`;
    } else if (registeredName) {
      return registeredName;
    } else if (barnName) {
      return barnName;
    } else {
      return `Horse #${horse.id}`;
    }
  };

  const getHorseSortKey = (horse) => {
    const r = (horse?.registered_name ?? '').toString().trim();
    const n = (horse?.name ?? '').toString().trim();
    return r || n || `Horse #${horse?.id}` || '';
  };

  const mareOwnerOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...[...mareOwners].sort(sortMareOwnersByLastName).map((owner) => ({ id: owner.id, label: owner.name })),
  ], [mareOwners]);

  const pregnancyStatusOptions = useMemo(() => (
    [...pregnancyStatuses]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((status) => ({ value: status.name.toLowerCase(), label: status.name }))
  ), [pregnancyStatuses]);

  // Generate year options for season filter
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);
  const seasonFilterOptions = ['', ...yearOptions];

  const statusFilterOptions = useMemo(() => [
    { value: '', label: 'All Statuses' },
    ...pregnancyStatuses.map((status) => ({ value: status.name.toLowerCase(), label: status.name })),
  ], [pregnancyStatuses]);


  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'flex-start', sm: 'space-between' },
          alignItems: 'center',
          gap: { xs: 1.5, sm: 0 },
          mb: 1,
          pt: 0.5,
        }}
      >
        <Typography variant="h4">Breeding Records</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            minWidth: { xs: 0, sm: 'auto' },
            width: { xs: 32, sm: 'auto' },
            height: { xs: 32, sm: 'auto' },
            px: { xs: 0, sm: 2 },
            '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Add Breeding
          </Box>
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
        <Autocomplete
          id="breeding-filter-season"
          size="small"
          disableClearable
          sx={{ minWidth: { xs: 90, sm: 120 } }}
          options={seasonFilterOptions}
          getOptionLabel={(year) => (year === '' ? 'All Seasons' : String(year))}
          filterOptions={createPrefixFilter((year) => (year === '' ? 'All Seasons' : String(year)))}
          value={filterSeason}
          onChange={(e, newValue) => setFilterSeason(newValue === '' ? '' : newValue)}
          renderInput={(params) => <TextField {...params} label="Season" />}
        />

        <Autocomplete
          id="breeding-filter-owner-type"
          size="small"
          disableClearable
          sx={{ minWidth: { xs: 105, sm: 150 } }}
          options={OWNER_TYPE_FILTER_OPTIONS}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.value === v?.value}
          filterOptions={createPrefixFilter((o) => o.label)}
          value={OWNER_TYPE_FILTER_OPTIONS.find((o) => o.value === filterOwnerType) ?? OWNER_TYPE_FILTER_OPTIONS[0]}
          onChange={(e, newValue) => setFilterOwnerType(newValue?.value ?? '')}
          renderInput={(params) => <TextField {...params} label="Owner Type" />}
        />

        <Autocomplete
          id="breeding-filter-status"
          size="small"
          disableClearable
          sx={{ minWidth: { xs: 105, sm: 150 } }}
          options={statusFilterOptions}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.value === v?.value}
          filterOptions={createPrefixFilter((o) => o.label)}
          value={statusFilterOptions.find((o) => o.value === filterStatus) ?? statusFilterOptions[0]}
          onChange={(e, newValue) => setFilterStatus(newValue?.value ?? '')}
          renderInput={(params) => <TextField {...params} label="Status" />}
        />
      </Box>
      {loadError ? (
        <Alert severity="error" sx={{ mb: 0.5 }}>
          {loadError}
        </Alert>
      ) : null}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={stickyActionsHeaderSx}>Actions</TableCell>
              <TableCell sx={stickyMareHeaderSx}>
                <TableSortLabel
                  active={sortBy === 'mare_name'}
                  direction={sortBy === 'mare_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('mare_name')}
                >
                  Mare
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortBy === 'stallion_name'}
                  direction={sortBy === 'stallion_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('stallion_name')}
                >
                  Stallion
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'breeding_date'}
                  direction={sortBy === 'breeding_date' ? sortOrder : 'asc'}
                  onClick={() => handleSort('breeding_date')}
                >
                  Breeding Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'breeding_method'}
                  direction={sortBy === 'breeding_method' ? sortOrder : 'asc'}
                  onClick={() => handleSort('breeding_method')}
                >
                  Method
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'pregnancy_status'}
                  direction={sortBy === 'pregnancy_status' ? sortOrder : 'asc'}
                  onClick={() => handleSort('pregnancy_status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'days_in_foal'}
                  direction={sortBy === 'days_in_foal' ? sortOrder : 'asc'}
                  onClick={() => handleSort('days_in_foal')}
                >
                  Days In Foal
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'due_date'}
                  direction={sortBy === 'due_date' ? sortOrder : 'asc'}
                  onClick={() => handleSort('due_date')}
                >
                  Due Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'actual_foaling_date'}
                  direction={sortBy === 'actual_foaling_date' ? sortOrder : 'asc'}
                  onClick={() => handleSort('actual_foaling_date')}
                >
                  Date foaled
                </TableSortLabel>
              </TableCell>
              <TableCell>Foal</TableCell>
              <TableCell>Confirmed In Foal</TableCell>
              <TableCell>Heartbeat Check</TableCell>
              <TableCell>60-Day Check</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'mare_owner_name'}
                  direction={sortBy === 'mare_owner_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('mare_owner_name')}
                >
                  Mare Owner
                </TableSortLabel>
              </TableCell>
              <TableCell>Season</TableCell>
              <TableCell>Stud Fee</TableCell>
              <TableCell>Stud Fee Paid</TableCell>
              <TableCell>Booking Fee</TableCell>
              <TableCell>Booking Fee Paid</TableCell>
              <TableCell>Mare Care Cost</TableCell>
              <TableCell>Transport Cost</TableCell>
              <TableCell>AQHA SBR</TableCell>
              <TableCell>APHA SBR</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={23} />}
            {!loading && (breedingRecords ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={23} align="center">
                  <Typography color="textSecondary">No breeding records found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              (breedingRecords ?? []).map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell align="center" sx={stickyActionsSx}>
                    <RowActionsMenu
                      actions={[
                        { key: 'view', label: 'View Detail', icon: <VisibilityIcon fontSize="small" />, onClick: () => navigate(`/breeding/${record.id}`) },
                        { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(record) },
                        { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(record.id), color: 'error' },
                      ]}
                    />
                  </TableCell>
                  <TableCell sx={stickyMareSx}>
                    {record.mare_name || record.mare_registered_name || `Mare ID: ${record.mare_id}`}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{record.stallion_registered_name || record.stallion_name || `Stallion ID: ${record.stallion_id}`}</TableCell>
                  <TableCell>
                    {record.breeding_date ? (
                      Array.isArray(record.breeding_dates) && record.breeding_dates.length > 1 ? (
                        <Tooltip title={`${record.breeding_dates.length} dates: ${record.breeding_dates.map((d) => formatDate(d)).join(', ')}`}>
                          <span>{formatDate(record.breeding_date)}</span>
                        </Tooltip>
                      ) : (
                        formatDate(record.breeding_date)
                      )
                    ) : '-'}
                  </TableCell>
                  <TableCell>{record.breeding_method || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={record.pregnancy_status}
                      size="small"
                      color={getStatusColor(record.pregnancy_status)}
                    />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const isBred = (record.pregnancy_status || '').toString().toLowerCase() === 'bred';
                      const isOpen = (record.pregnancy_status || '').toString().toLowerCase() === 'open';
                      const bredSx = isBred ? { color: 'text.disabled' } : undefined;

                      if (isOpen) {
                        return '-';
                      }

                      const days = calculateDaysInFoal(record);
                      if (days === null) {
                        return '-';
                      }
                      const monthsDaysStr = formatMonthsAndDays(days);
                      return (
                        <Box>
                          <Typography variant="body2" sx={bredSx}>
                            {days}
                          </Typography>
                          {monthsDaysStr && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', ...bredSx }}>
                              {monthsDaysStr}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const isBred = (record.pregnancy_status || '').toString().toLowerCase() === 'bred';
                      const isOpen = (record.pregnancy_status || '').toString().toLowerCase() === 'open';
                      const bredSx = isBred ? { color: 'text.disabled' } : undefined;

                      // Signals that the cell has a hover popup behind it.
                      const hoverSx = {
                        cursor: 'help',
                        textDecoration: 'underline dotted',
                        textDecorationColor: (t) => t.palette.secondary.main,
                        textUnderlineOffset: '3px',
                      };

                      const overrideNote = record.due_date_based_on_override
                        ? (record.mare_gestation_days_override != null
                          ? `Calculated using mare's gestation override (${record.mare_gestation_days_override} days)`
                          : "Calculated using mare's gestation override")
                        : null;

                      let content;
                      if (isOpen || !record.due_date) {
                        // No due date to show, but her history is still worth reaching.
                        content = (
                          <Typography component="span" variant="body2" sx={{ cursor: 'help' }}>
                            -
                          </Typography>
                        );
                      } else if (overrideNote) {
                        content = (
                          <Typography component="span" sx={{ ...bredSx, cursor: 'help' }}>
                            <Box component="span" sx={hoverSx}>{formatDate(record.due_date)}</Box>
                            <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 0.5 }}>
                              (override)
                            </Typography>
                          </Typography>
                        );
                      } else {
                        content = (
                          <Typography component="span" variant="body2" sx={{ ...bredSx, ...hoverSx }}>
                            {formatDate(record.due_date)}
                          </Typography>
                        );
                      }

                      return (
                        <MareGestationHistoryTooltip
                          mareId={record.mare_id}
                          excludeRecordId={record.id}
                          entry={mareHistory[record.mare_id]}
                          overrideNote={overrideNote}
                          onOpen={handleMareHistoryOpen}
                        >
                          {content}
                        </MareGestationHistoryTooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {formatDate(record.actual_foaling_date) ?? '-'}
                  </TableCell>
                  <TableCell>{record.foal_name || '-'}</TableCell>
                  <TableCell>{formatDate(record.confirmed_in_foal_date) || '-'}</TableCell>
                  <TableCell>{formatDate(record.heart_beat_check) || '-'}</TableCell>
                  <TableCell>{formatDate(record.sixty_day_check) || '-'}</TableCell>
                  <TableCell>{record.mare_owner_name || '-'}</TableCell>
                  <TableCell>{record.breeding_season || '-'}</TableCell>
                  <TableCell>{record.stud_fee != null ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(record.stud_fee) : '-'}</TableCell>
                  <TableCell>{record.stud_fee_paid == null ? '-' : record.stud_fee_paid ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{record.booking_fee != null ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(record.booking_fee) : '-'}</TableCell>
                  <TableCell>{record.booking_fee_paid == null ? '-' : record.booking_fee_paid ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{record.mare_care_cost != null ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(record.mare_care_cost) : '-'}</TableCell>
                  <TableCell>{record.transport_cost != null ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(record.transport_cost) : '-'}</TableCell>
                  <TableCell>{formatDate(record.aqha_sbr) || '-'}</TableCell>
                  <TableCell>{formatDate(record.apha_sbr) || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      {/* Add/Edit Breeding Record Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRecord ? 'Edit Breeding Record' : 'Add Breeding Record'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Grid container spacing={2}>
              {/* Horses Section */}
              <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
                <Typography variant="subtitle1" color="primary">
                  Horses
                </Typography>
              </Grid>

              {/* Mare with Add Button */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={[...mares].sort((a, b) => getHorseSortKey(a).localeCompare(getHorseSortKey(b)))}
                    getOptionLabel={getHorseDisplayName}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter(getHorseDisplayName)}
                    value={mares.find((mare) => mare.id === formData.mare_id) ?? null}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, mare_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Mare" required />}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenMareModal(true)}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Stallion with Add Button */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={[...stallions].sort((a, b) => getHorseSortKey(a).localeCompare(getHorseSortKey(b)))}
                    getOptionLabel={getHorseDisplayName}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter(getHorseDisplayName)}
                    value={stallions.find((stallion) => stallion.id === formData.stallion_id) ?? null}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, stallion_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Stallion" required />}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenStallionModal(true)}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Mare Owner with Add Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={mareOwnerOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((o) => o.label)}
                    value={mareOwnerOptions.find((o) => o.id === formData.mare_owner_id) ?? mareOwnerOptions[0]}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, mare_owner_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Mare Owner (Optional)" />}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenMareOwnerModal(true)}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Breeding Details */}
              <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
                  Breeding Details
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  fullWidth
                  disableClearable
                  options={[...breedingMethods].sort((a, b) => (a.name || '').localeCompare(b.name || ''))}
                  getOptionLabel={(method) => method.name}
                  isOptionEqualToValue={(o, v) => o.name === v?.name}
                  filterOptions={createPrefixFilter((method) => method.name)}
                  value={breedingMethods.find((method) => method.name === formData.breeding_method) ?? null}
                  onChange={(e, newValue) =>
                    setFormData((prev) => ({ ...prev, breeding_method: newValue?.name ?? '' }))
                  }
                  renderInput={(params) => <TextField {...params} label="Breeding Method" required />}
                />
              </Grid>

              <Grid item xs={12}>
                {((formData.breeding_dates && formData.breeding_dates.length) ? formData.breeding_dates : ['']).map((dateVal, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                    <TextField
                  size="small"
                      label={`Date Bred ${idx + 1}`}
                      type="date"
                      value={dateVal}
                      onChange={(e) => handleBreedingDateChange(idx, e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveBreedingDate(idx)}
                      disabled={(formData.breeding_dates || []).length <= 1}
                      color="error"
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddBreedingDate}
                >
                  Add Date
                </Button>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="breeding_season"
                  label="Breeding Season (Year)"
                  type="number"
                  value={formData.breeding_season}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  fullWidth
                  disableClearable
                  options={pregnancyStatusOptions}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.value === v?.value}
                  filterOptions={createPrefixFilter((o) => o.label)}
                  value={pregnancyStatusOptions.find((o) => o.value === formData.pregnancy_status) ?? null}
                  onChange={(e, newValue) =>
                    setFormData((prev) => ({ ...prev, pregnancy_status: newValue?.value ?? '' }))
                  }
                  renderInput={(params) => <TextField {...params} label="Pregnancy Status" />}
                />
              </Grid>

              {/* Pregnancy Tracking */}
              <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
                  Pregnancy Tracking
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="confirmed_in_foal_date"
                  label="Confirmed In Foal Date"
                  type="date"
                  value={formData.confirmed_in_foal_date}
                  onChange={handleInputChange}
                  error={confirmedInFoalMissingRequiredDates}
                  helperText={confirmedInFoalMissingRequiredDates ? confirmedInFoalHelperText : undefined}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="heart_beat_check"
                  label="Heart Beat Check"
                  type="date"
                  value={formData.heart_beat_check}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="forty_five_day_check"
                  label="45 Day Check"
                  type="date"
                  value={formData.forty_five_day_check}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="sixty_day_check"
                  label="60 Day Check"
                  type="date"
                  value={formData.sixty_day_check}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="actual_foaling_date"
                  label="Date foaled"
                  type="date"
                  value={formData.actual_foaling_date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              {/* Financial Information */}
              <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
                  Financial Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="stud_fee"
                  label="Stud Fee"
                  type="number"
                  value={formData.stud_fee}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="booking_fee"
                  label="Booking Fee"
                  type="number"
                  value={formData.booking_fee}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="stud_fee_paid_date"
                  label="Stud Fee Paid Date"
                  type="date"
                  value={formData.stud_fee_paid_date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="booking_fee_paid_date"
                  label="Booking Fee Paid Date"
                  type="date"
                  value={formData.booking_fee_paid_date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="mare_care_cost"
                  label="Mare Care Cost"
                  type="number"
                  value={formData.mare_care_cost}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="transport_cost"
                  label="Transport Cost"
                  type="number"
                  value={formData.transport_cost}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              {/* SBR Information */}
              <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
                  SBR Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="aqha_sbr"
                  label="AQHA SBR Date"
                  type="date"
                  value={formData.aqha_sbr}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="apha_sbr"
                  label="APHA SBR Date"
                  type="date"
                  value={formData.apha_sbr}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="notes"
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.mare_id ||
              !formData.stallion_id ||
              !formData.breeding_method ||
              confirmedInFoalMissingRequiredDates
            }
          >
            {editingRecord ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Mare Modal */}
      <AddHorseModal
        open={openMareModal}
        onClose={() => setOpenMareModal(false)}
        onSuccess={handleAddMare}
        defaultType="Mare"
      />

      {/* Add Stallion Modal */}
      <AddHorseModal
        open={openStallionModal}
        onClose={() => setOpenStallionModal(false)}
        onSuccess={handleAddStallion}
        defaultType="Stallion"
      />

      {/* Add Mare Owner Modal */}
      <AddMareOwnerModal
        open={openMareOwnerModal}
        onClose={() => setOpenMareOwnerModal(false)}
        onSuccess={handleAddMareOwner}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Breeding Record"
        message="Permanently delete this breeding record? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default BreedingPage;
